import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef, startTransition } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import PermissionGate from '../../../components/common/PermissionGate';
import {
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  UserX,
  UserCheck,
  ArrowRight,
  Plus,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  MapPin,
  Calendar,
  Users,
  User,
  AlertCircle,
  FileText,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Trash2,
} from 'lucide-react';
import hrService from '../../../api/services/hrService';
import locationService from '../../../api/services/locationService';
import departmentRoleService from '../../../api/services/departmentRoleService';
import { employeeTeamService } from '../../../api/crm/config/configService';
import Button from '../../../components/common/Button/Button';
import { formatDate } from '../../../utils/stringUtils';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import { exportToCSV, generateExportFilename, downloadFromBackend } from '../../../utils/exportUtils';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import useUrlTableState from '../../../hooks/useUrlTableState';

const EmployeeList = ({ onViewProfile, onEditEmployee, onAddEmployee }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryClient = useQueryClient();

  // State management
  const [deactivatingEmployee, setDeactivatingEmployee] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [employeeToDeactivate, setEmployeeToDeactivate] = useState(null);
  const [actionType, setActionType] = useState('deactivate'); // 'activate', 'deactivate', or 'delete'
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [deletingEmployee, setDeletingEmployee] = useState(false);
  const [syncingUsers, setSyncingUsers] = useState(false);

  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 20,
    search: '',
    department: '',
    team: '',
    status: '',
    startDate: '',
    endDate: '',
    sortBy: '',
    sortOrder: 'asc'
  }), []);

  const {
    filters: empFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updateEmpFilter,
    updatePage: updateEmpPage,
    updateLimit: updateEmpLimit,
    updateSort: updateEmpSort,
    resetFilters: resetEmpFilters
  } = useUrlTableState(defaultFilters);

  const searchTerm = searchQuery;
  const setSearchTerm = setSearchQuery;
  const debouncedSearchTerm = empFilters.search || '';

  const currentPage = empFilters.page;
  const setCurrentPage = useCallback((page) => {
    updateEmpPage(page);
  }, [updateEmpPage]);

  const itemsPerPage = empFilters.limit;
  const setItemsPerPage = useCallback((limit) => {
    updateEmpLimit(limit);
  }, [updateEmpLimit]);

  const sortField = empFilters.sortBy || null;
  const sortDirection = empFilters.sortOrder || 'asc';

  const dateRange = useMemo(() => ({
    startDate: empFilters.startDate || '',
    endDate: empFilters.endDate || ''
  }), [empFilters.startDate, empFilters.endDate]);

  const setDateRange = useCallback((range) => {
    updateEmpFilter({ startDate: range.startDate, endDate: range.endDate });
  }, [updateEmpFilter]);

  const filters = useMemo(() => ({
    department: empFilters.department || '',
    team: empFilters.team || '',
    status: empFilters.status || '',
  }), [empFilters.department, empFilters.team, empFilters.status]);

  // Selection state
  const [selectedRows, setSelectedRows] = useState(new Set());
  // Export state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);

  const [showFilters, setShowFilters] = useState(false);
  // Locations state removed
  const searchInputRef = useRef(null);
  const shouldMaintainFocusRef = useRef(false);
  const isSearchFocusedRef = useRef(false);
  const cursorPositionRef = useRef(null);
  const focusTimeoutRef = useRef(null);
  const deptDropdownRef = useRef(null);
  const [isDeptDropdownOpen, setIsDeptDropdownOpen] = useState(false);

  // Filter options - now using actual data from backend


  // ─── React Query: fetch employees ───────────────────────────────────────────
  const employeesQueryKey = [
    'employees',
    currentPage,
    itemsPerPage,
    filters.department,
    filters.team,
    filters.status,
    debouncedSearchTerm,
    dateRange.startDate,
    dateRange.endDate,
  ];

  const fetchEmployees = async () => {
    const queryParams = new URLSearchParams({
      page: currentPage.toString(),
      limit: itemsPerPage.toString(),
    });

    if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
      queryParams.append('search', debouncedSearchTerm.trim());
    }
    if (filters.department && filters.department !== 'All') {
      queryParams.append('department', filters.department);
    }
    if (filters.team && filters.team !== 'All') {
      queryParams.append('team', filters.team);
    }
    if (filters.status && filters.status !== 'All') {
      queryParams.append('status', filters.status.toLowerCase());
    }
    if (dateRange.startDate) {
      queryParams.append('startDate', dateRange.startDate);
    }
    if (dateRange.endDate) {
      queryParams.append('endDate', dateRange.endDate);
    }

    const queryString = queryParams.toString();

    const result = await hrService.getEmployees(queryString);

    if (!result.success) {
      throw new Error(result.error || 'Failed to load employees. Please try again.');
    }

    let employeesData = [];
    let totalCount = 0;

    if (Array.isArray(result.data)) {
      employeesData = result.data;
      totalCount = result.data.length;
    } else if (result.data && Array.isArray(result.data.employees)) {
      employeesData = result.data.employees;
      totalCount =
        result.data.pagination?.totalEmployees ||
        result.data.pagination?.total ||
        result.data.total ||
        employeesData.length;
    } else if (result.data && Array.isArray(result.data.data)) {
      employeesData = result.data.data;
      totalCount =
        result.data.pagination?.totalEmployees ||
        result.data.pagination?.total ||
        result.data.total ||
        employeesData.length;
    } else {
      throw new Error('Invalid data format received from server');
    }

    const transformedEmployees = employeesData.map((emp) => {
      const statusValue = emp.employmentInfo?.status || emp.status;
      const calculatedStatus =
        statusValue === 'active'
          ? 'Active'
          : statusValue === 'inactive'
            ? 'Inactive'
            : statusValue === 'transferred'
              ? 'Transferred'
              : statusValue === 'exited'
                ? 'Exited'
                : statusValue || 'Unknown';

      return {
        id: emp._id,
        employeeId: emp.employeeId,
        name: `${emp.personalInfo?.firstName || ''} ${emp.personalInfo?.lastName || ''}`.trim().replace(/\b\w/g, l => l.toUpperCase()),
        email: emp.user?.email || emp.contactInfo?.personalEmail || '',
        phone: emp.contactInfo?.personalPhone || emp.user?.phone || '',
        department: emp.employmentInfo?.department || '',
        team: emp.employmentInfo?.team || '',
        role: emp.employmentInfo?.position || '',
        location: (typeof emp.employmentInfo?.workLocation === 'object' ? emp.employmentInfo?.workLocation?.name : emp.employmentInfo?.workLocation) || '',
        doj: emp.employmentInfo?.joinDate ? new Date(emp.employmentInfo.joinDate).toISOString().split('T')[0] : '',
        status: calculatedStatus,
        manager: emp.employmentInfo?.reportingManager
          ? `${emp.employmentInfo.reportingManager.personalInfo?.firstName || ''} ${emp.employmentInfo.reportingManager.personalInfo?.lastName || ''}`.trim().replace(/\b\w/g, l => l.toUpperCase())
          : 'Not Assigned',
        avatar: emp.systemInfo?.profilePicture || null,
        hasDocuments: emp.hasDocuments || false,
      };
    });

    return { employees: transformedEmployees, total: totalCount };
  };

  const {
    data: employeesData,
    isLoading: loading,
    error: employeesError,
    refetch: refetchEmployees,
  } = useQuery({
    queryKey: employeesQueryKey,
    queryFn: fetchEmployees,
    placeholderData: (prev) => prev,
    staleTime: 0,
  });

  // Derive employees & totalEmployees from query result
  const employees = employeesData?.employees ?? [];
  const totalEmployees = employeesData?.total ?? 0;
  const error = employeesError ? (employeesError.message || 'Failed to load employees. Please try again.') : null;

  const handleSyncUsers = async () => {
    setSyncingUsers(true);
    try {
      const res = await hrService.syncBiometricUsers();
      if (res.success) {
        toast.success(res.message || 'Biometric user sync started in background!');
        setTimeout(() => {
          refetchEmployees();
        }, 2500);
      } else {
        toast.error(res.error || 'Failed to start user sync.');
      }
    } catch (err) {
      toast.error('Failed to trigger user sync.');
    } finally {
      setSyncingUsers(false);
    }
  };

  // Clear selection when query key changes (new page / filter)
  useEffect(() => {
    setSelectedRows(new Set());
  }, [currentPage, filters.department, filters.team, filters.status, debouncedSearchTerm, itemsPerPage, dateRange.startDate, dateRange.endDate]);

  // Maintain focus on search input after re-renders
  useEffect(() => {
    // Only maintain focus if user was actively typing (shouldMaintainFocusRef is true)
    // This prevents unnecessary focus restoration when user isn't typing
    if (searchInputRef.current && shouldMaintainFocusRef.current) {
      const input = searchInputRef.current;
      const savedCursorPos = cursorPositionRef.current;

      // Use multiple strategies to ensure focus is maintained
      const restoreFocus = () => {
        if (input && document.contains(input)) {
          // Check if input is still in the DOM and not already focused
          if (document.activeElement !== input && isSearchFocusedRef.current) {
            input.focus();
          }

          // Restore cursor position if we have a saved position
          if (savedCursorPos !== null && savedCursorPos !== undefined && document.activeElement === input) {
            const maxPos = input.value.length;
            const pos = Math.min(savedCursorPos, maxPos);
            // Use setTimeout to ensure the cursor position is set after focus
            setTimeout(() => {
              if (input && document.activeElement === input) {
                try {
                  input.setSelectionRange(pos, pos);
                } catch (e) {
                  // Ignore errors if input is not in a valid state
                }
              }
            }, 0);
          }
        }
      };

      // Try to restore focus immediately
      restoreFocus();

      // Also try after a short delay to catch any async updates
      requestAnimationFrame(() => {
        restoreFocus();
        setTimeout(() => {
          restoreFocus();
          // Reset the flag after we've tried to restore focus
          if (document.activeElement === input) {
            shouldMaintainFocusRef.current = false;
          }
        }, 10);
      });
    }
  }, [employees, searchTerm]);

  // Restore focus after loading completes (when employees are loaded)
  // Use useLayoutEffect for synchronous execution before browser paint
  useLayoutEffect(() => {
    if (!loading && searchInputRef.current && shouldMaintainFocusRef.current && isSearchFocusedRef.current) {
      const input = searchInputRef.current;
      const savedCursorPos = cursorPositionRef.current;

      // Immediately try to restore focus synchronously
      if (input && document.contains(input) && document.activeElement !== input) {
        input.focus();

        // Restore cursor position immediately
        if (savedCursorPos !== null && savedCursorPos !== undefined) {
          const maxPos = input.value.length;
          const pos = Math.min(savedCursorPos, maxPos);
          try {
            input.setSelectionRange(pos, pos);
          } catch (e) {
            // Ignore errors
          }
        }
      }
    }
  }, [loading, employees]);

  // Also use useEffect as a fallback for async focus restoration
  useEffect(() => {
    if (!loading && searchInputRef.current && shouldMaintainFocusRef.current && isSearchFocusedRef.current) {
      const input = searchInputRef.current;
      const savedCursorPos = cursorPositionRef.current;

      // Use multiple attempts to restore focus after data loads (fallback)
      const attempts = [10, 50, 100, 200]; // Try at different delays

      attempts.forEach((delay) => {
        setTimeout(() => {
          if (input && document.contains(input) && isSearchFocusedRef.current) {
            if (document.activeElement !== input) {
              input.focus();
            }

            // Restore cursor position
            if (savedCursorPos !== null && savedCursorPos !== undefined && document.activeElement === input) {
              const maxPos = input.value.length;
              const pos = Math.min(savedCursorPos, maxPos);
              try {
                input.setSelectionRange(pos, pos);
              } catch (e) {
                // Ignore errors
              }
            }
          }
        }, delay);
      });
    }
  }, [loading, employees]);

  // ─── React Query: fetch departments ─────────────────────────────────────────
  const fetchDepartments = async () => {
    const result = await departmentRoleService.getDepartments({ isActive: 'true' });

    let departmentsData = [];
    if (result.responseCode === 200 && result.data) {
      departmentsData = Array.isArray(result.data) ? result.data : [];
    } else if (Array.isArray(result)) {
      departmentsData = result;
    } else if (result.data && Array.isArray(result.data)) {
      departmentsData = result.data;
    }

    const activeDepartments = departmentsData.filter(
      (dept) => dept.isActive !== false && dept.status !== 'Inactive'
    );
    return activeDepartments;
  };

  const { data: departments = [], isLoading: loadingDepartments } = useQuery({
    queryKey: ['departments', 'active'],
    queryFn: fetchDepartments,
    staleTime: 5 * 60 * 1000, // 5 min – departments rarely change
  });

  const fetchTeams = async () => {
    const result = await employeeTeamService.getAll({ isActive: true });
    if (result?.success && Array.isArray(result.data)) {
      return result.data
        .filter((team) => team?.name)
        .map((team) => team.name);
    }
    return [];
  };

  const { data: teams = [] } = useQuery({
    queryKey: ['employee-teams', 'active'],
    queryFn: fetchTeams,
    staleTime: 5 * 60 * 1000,
  });

  // Filter options - now using actual data from backend
  const filterOptions = {
    departments: ['All Departments', ...departments.map(dept => dept.name || dept)],
    teams: ['All Teams', ...teams],
    // Locations filter removed
    statuses: ['All Status', 'Active', 'Inactive', 'Exited']
  };

  // ─── React Query: mutations ──────────────────────────────────────────────────
  const invalidateEmployees = () => queryClient.invalidateQueries({ queryKey: ['employees'] });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids) => hrService.deleteEmployeesBulk(ids),
    onSuccess: (result) => {
      if (result.success) {
        toast.success(result.data?.responseMessage || `${selectedRows.size} employee(s) deleted successfully`);
        setSelectedRows(new Set());
        invalidateEmployees();
      } else {
        toast.error(result.error);
      }
    },
    onError: (err) => {
      toast.error('An error occurred during bulk delete');
    },
  });

  const bulkStatusMutation = useMutation({
    mutationFn: ({ ids, status }) => hrService.updateEmployeesStatusBulk(ids, status),
    onSuccess: (result) => {
      if (result.success) {
        toast.success(result.data?.responseMessage || `${selectedRows.size} employee(s) updated successfully`);
        setSelectedRows(new Set());
        invalidateEmployees();
      } else {
        toast.error(result.error);
      }
    },
    onError: (err, { ids }, context) => {
      toast.error('An error occurred during bulk status update');
    },
  });

  const deleteEmployeeMutation = useMutation({
    mutationFn: (id) => hrService.deleteEmployee(id),
    onSuccess: (result) => {
      if (result.success) {
        toast.success('Employee record deleted successfully');
        setEmployeeToDelete(null);
        setActionType('deactivate');
        invalidateEmployees();
      } else {
        toast.error(result.error);
      }
    },
    onError: (err) => {
      toast.error('An error occurred during deletion');
      setEmployeeToDelete(null);
      setActionType('deactivate');
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) => hrService.updateEmployee(id, { status }),
    onSuccess: (result, { id, status }) => {
      const actionLabel = status === 'active' ? 'activated' : 'deactivated';
      if (result.success) {
        toast.success(`Employee ${actionLabel} successfully!`);
        setEmployeeToDeactivate(null);
        setDeactivatingEmployee(null);
        setActionType('deactivate');
        invalidateEmployees();
      } else {
        toast.error(`Failed to ${actionLabel} employee: ${result.error}`);
        setDeactivatingEmployee(null);
        setEmployeeToDeactivate(null);
        setActionType('deactivate');
      }
    },
    onError: (err, { id, status }) => {
      const actionLabel = status === 'active' ? 'activate' : 'deactivate';
      toast.error(`Failed to ${actionLabel} employee. Please try again.`);
      setDeactivatingEmployee(null);
      setEmployeeToDeactivate(null);
      setActionType('deactivate');
    },
  });

  // Derived loading state for mutations (used by confirm dialog spinner)
  const isMutating =
    bulkDeleteMutation.isPending ||
    bulkStatusMutation.isPending ||
    deleteEmployeeMutation.isPending ||
    updateStatusMutation.isPending;

  // ─── Auto-focus search on mount ─────────────────────────────────────────────
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // ─── Click-outside handler for dept dropdown ─────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (deptDropdownRef.current && !deptDropdownRef.current.contains(event.target)) {
        setIsDeptDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Click-outside handler for inline export format bar
  useEffect(() => {
    const onMouseDown = (event) => {
      if (!showInlineSelectedFormats) return;
      const target = event.target;
      const wrap = selectedExportWrapRef.current;
      const bar = target.closest('[data-export-format-bar]');
      if (wrap && !wrap.contains(target) && !bar) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  // Handle selectedRows.size changing to hide inline export formats
  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.size]);

  // Handle fade + slide animation state for inline format bar
  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  // Load locations for filter dropdown
  // loadLocations function removed


  // Filter and search employees (now handled by backend)
  const filteredEmployees = useMemo(() => {
    let filtered = Array.isArray(employees) ? [...employees] : [];

    // Other filters (department and status are handled by backend, but keeping location filter)
    // Other filters (department and status are handled by backend, but keeping location filter)
    // Location filter logic removed

    // Sorting – only apply when user has explicitly chosen a sort field
    if (sortField) {
      filtered.sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];

        if (sortField === "doj") {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        } else if (typeof aValue === "string" && typeof bValue === "string") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (sortDirection === "asc") {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    }

    return filtered;
  }, [employees, sortField, sortDirection]);

  // Pagination - using server-side pagination
  const totalPages = Math.ceil(totalEmployees / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + employees.length;
  const currentEmployees = filteredEmployees; // Use filtered and sorted data

  // Handle search - allow alphanumeric, spaces, and email characters (@, ., -, _, +)
  const handleSearch = useCallback((e) => {
    const input = e.target;
    const value = input.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);

    // Store cursor position before state update
    cursorPositionRef.current = input.selectionStart;

    // Mark that we should maintain focus since user is typing
    shouldMaintainFocusRef.current = true;
    isSearchFocusedRef.current = true;

    // Clear any existing timeout
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
    }

    // Keep focus flag true for a short period after typing stops
    // This ensures focus is maintained even during rapid typing
    focusTimeoutRef.current = setTimeout(() => {
      // Only reset if user is no longer focused on the input
      if (document.activeElement !== searchInputRef.current) {
        shouldMaintainFocusRef.current = false;
      }
    }, 500); // 500ms after typing stops

    setSearchTerm(value);
  }, [setSearchTerm]);

  // Handle search input focus
  const handleSearchFocus = useCallback(() => {
    isSearchFocusedRef.current = true;
  }, []);

  // Handle search input blur
  const handleSearchBlur = useCallback(() => {
    // Only mark as not focused if user explicitly clicked away
    // We'll check this after a short delay to see if focus was restored
    setTimeout(() => {
      if (document.activeElement !== searchInputRef.current) {
        isSearchFocusedRef.current = false;
        shouldMaintainFocusRef.current = false;
      }
    }, 100);
  }, []);

  // Handle filter change
  const handleFilterChange = useCallback((filterType, value) => {
    updateEmpFilter({ [filterType]: value });
  }, [updateEmpFilter]);

  // Clear all filters and search
  const handleClearFilters = useCallback(() => {
    resetEmpFilters();
  }, [resetEmpFilters]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      (searchTerm || '').trim() !== '' ||
      filters.department !== '' ||
      filters.team !== '' ||
      filters.status !== '' ||
      dateRange.startDate !== '' ||
      dateRange.endDate !== ''
    );
  }, [searchTerm, filters.department, filters.team, filters.status, dateRange.startDate, dateRange.endDate]);

  // Handle sort
  const handleSort = useCallback((field) => {
    if (sortField === field) {
      updateEmpSort(field, sortDirection === "asc" ? "desc" : "asc");
    } else {
      updateEmpSort(field, "asc");
    }
  }, [sortField, sortDirection, updateEmpSort]);

  // Handle page change
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, [setCurrentPage]);

  const handleItemsPerPageChange = useCallback((value) => {
    const parsed = typeof value === 'number' ? value : parseInt(value, 10);
    if (Number.isNaN(parsed)) return;
    setItemsPerPage(parsed);
  }, [setItemsPerPage]);


  // Download all employees as CSV
  // Selection Handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = currentEmployees.map((emp) => emp.id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const isAllSelected = currentEmployees.length > 0 && currentEmployees.every((emp) => selectedRows.has(emp.id));

  // Data Transformation for Export
  const mapEmployeesForExport = (data) => {
    return data.map((emp) => ({
      'Employee ID': emp.employeeId || '',
      'Name': emp.name || '',
      'Email': emp.email || '',
      'Phone': emp.phone || '',
      'Department': emp.department || '',
      'Position': emp.role || '',
      'Location': emp.location || '',
      'Status': emp.status || '',
      'Joined Date': emp.doj ? new Date(emp.doj).toLocaleDateString('en-IN') : ''
    }));
  };

  // Build base export filters for backend API
  const baseExportFilters = useMemo(() => {
    const apiFilters = {
      search: debouncedSearchTerm || undefined,
      department: filters.department || undefined,
      team: filters.team || undefined,
      status: filters.status || undefined,
      startDate: dateRange.startDate || undefined,
      endDate: dateRange.endDate || undefined,
    };

    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });

    return apiFilters;
  }, [debouncedSearchTerm, filters.department, filters.team, filters.status, dateRange.startDate, dateRange.endDate]);

  // Get current page employee IDs
  const currentPageEmployeeIds = useMemo(
    () =>
      currentEmployees
        .map((emp) => {
          const raw = emp?._id ?? emp?.id ?? emp?.employeeId;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean),
    [currentEmployees]
  );

  // Unified backend export function using /exports/generate endpoint
  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const token = localStorage.getItem('auth_token');
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageEmployeeIds)
          : [];
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module: 'employees',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? currentPage,
            limit: overrideLimit ?? itemsPerPage,
            sortBy: sortField,
            sortOrder: sortDirection,
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `employees-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      currentPage,
      itemsPerPage,
      sortField,
      sortDirection,
      currentPageEmployeeIds,
    ]
  );

  // Handle export for selected rows with format selection
  const handleEmployeesSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error('No selected employees to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success('Export started.');
        setShowInlineSelectedFormats(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  // Handle scoped export from modal (all, filtered, currentPage, dateRange)
  const handleEmployeesScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }
      setExportLoading(true);
      try {
        if (scope === 'dateRange') {
          const { startDate, endDate } = meta;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: itemsPerPage,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageEmployeeIds,
          });
        } else {
          return;
        }

        toast.success('Export started.');
        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      currentEmployees.length,
      totalEmployees,
      baseExportFilters,
      requestBackendExport,
      itemsPerPage,
      currentPageEmployeeIds,
    ]
  );

  // Export Handlers
  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  // Legacy handlers for backward compatibility (deprecated)
  const handleExportCurrentPage = () => {
    const exportData = mapEmployeesForExport(currentEmployees);
    exportToCSV(exportData, 'employees_page', {
      title: 'Employee List',
      type: 'Current Page'
    });
    setShowExportModal(false);
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("type", `Date Range (${startDate} to ${endDate})`);
      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);
      if (debouncedSearchTerm) queryParams.append("search", debouncedSearchTerm);
      if (filters.department) queryParams.append("department", filters.department);
      if (filters.status) queryParams.append("status", filters.status);

      const url = `${process.env.REACT_APP_API_BASE_URL}/hr/employees/export?${queryParams.toString()}`;
      const filename = generateExportFilename('employees_range');
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
      toast.success('Export started successfully');
    } catch (error) {
      toast.error("An error occurred during export");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("type", "All Data");
      if (debouncedSearchTerm) queryParams.append("search", debouncedSearchTerm);
      if (filters.department) queryParams.append("department", filters.department);
      if (filters.status) queryParams.append("status", filters.status);

      const url = `${process.env.REACT_APP_API_BASE_URL}/hr/employees/export?${queryParams.toString()}`;
      const filename = generateExportFilename('all_employees');
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
      toast.success('All employees export started');
    } catch (error) {
      toast.error("An error occurred during export");
    } finally {
      setExportLoading(false);
    }
  };

  // Deprecated: redirecting to new logic
  const downloadAllEmployees = handleExportClick;


  // Handle quick actions
  const handleViewEmployee = (employeeId) => {
    if (onViewProfile) {
      onViewProfile(employeeId);
      return;
    }
    try {
      navigate(`/employee_profile/${employeeId}`, { state: { from: location.pathname + location.search } });
    } catch (error) {
    }
  };

  const handleEditEmployee = (employeeId) => {
    if (onEditEmployee) {
      onEditEmployee(employeeId);
      return;
    }
    try {
      navigate(`/edit_employee/${employeeId}`, { state: { from: location.pathname + location.search } });
    } catch (error) {
    }
  };

  const handleDeactivateEmployee = (employeeId, employeeStatus, event) => {
    const isActive = employeeStatus === 'Active';
    const action = isActive ? 'deactivate' : 'activate';

    // Prevent event propagation and default behavior
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Prevent multiple simultaneous calls for the same employee
    if (deactivatingEmployee === employeeId) {
      return;
    }

    // Prevent action if dialog is already open
    if (showConfirmDialog) {
      return;
    }

    setEmployeeToDeactivate(employeeId);
    setActionType(action);
    setShowConfirmDialog(true);
  };

  const handleDeleteEmployee = (employeeId, event) => {
    if (event) event.stopPropagation();

    setEmployeeToDelete(employeeId);
    setActionType('delete');
    setIsBulkDelete(false);
    setShowConfirmDialog(true);
  };

  const handleBulkActionClicked = (action) => {
    if (selectedRows.size === 0) {
      toast.error(`Please select at least one employee to ${action}`);
      return;
    }

    setActionType(action);
    setIsBulkDelete(true);
    setShowConfirmDialog(true);
  };

  const confirmDeactivate = () => {
    setShowConfirmDialog(false);

    if (isBulkDelete) {
      const idsArray = Array.from(selectedRows);
      if (actionType === 'delete') {
        bulkDeleteMutation.mutate(idsArray, {
          onSettled: () => {
            setIsBulkDelete(false);
            setActionType('deactivate');
          },
        });
      } else if (actionType === 'activate' || actionType === 'deactivate') {
        const status = actionType === 'activate' ? 'active' : 'inactive';
        bulkStatusMutation.mutate({ ids: idsArray, status }, {
          onSettled: () => {
            setIsBulkDelete(false);
            setActionType('deactivate');
          },
        });
      }
      return;
    }

    if (actionType === 'delete') {
      deleteEmployeeMutation.mutate(employeeToDelete);
      return;
    }

    if (!employeeToDeactivate) return;

    const isActivating = actionType === 'activate';
    const newStatus = isActivating ? 'active' : 'inactive';
    setDeactivatingEmployee(employeeToDeactivate);
    updateStatusMutation.mutate({ id: employeeToDeactivate, status: newStatus });
  };

  const cancelDeactivate = () => {
    setShowConfirmDialog(false);
    setEmployeeToDeactivate(null);
    setEmployeeToDelete(null);
    setIsBulkDelete(false);
    setActionType('deactivate');
  };

  const handleManageDocuments = (employeeId) => {
    try {
      navigate(`/employee_profile/${employeeId}?tab=documents`, { state: { from: location.pathname + location.search } });
    } catch (error) {
    }
  };


  const handleAddEmployee = () => {
    if (onAddEmployee) {
      onAddEmployee();
      return;
    }
    try {
      navigate("/add_employee", { state: { from: location.pathname + location.search } });
    } catch (error) {
    }
  };

  // Helper for UI display logic
  const getDisplayStatus = (status) => {
    if (!status) return "";
    const lower = status.toLowerCase();
    if (lower === "terminated" || lower === "exited") return "Exited";
    return status;
  };

  // Get status color
  const getStatusColor = (status) => {
    const lower = status?.toLowerCase() || "";
    switch (lower) {
      case "active":
        return "bg-green-100 text-green-800";
      case "inactive":
        return "bg-red-100 text-red-800";
      case "transferred":
        return "bg-blue-100 text-blue-800";
      case "exited":
      case "terminated":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };


  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <div className="text-red-600 mb-4">
          <AlertCircle className="h-12 w-12 mx-auto mb-2" />
          <h3 className="text-lg font-semibold">Error Loading Employees</h3>
          <p className="text-sm">{error}</p>
        </div>
        <button
          onClick={() => refetchEmployees()}
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employee List</h1>
          <p className="text-gray-600 mt-1">
            Manage and view all employee records
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <PermissionGate permission="employee_management" action="read">
            <div className="relative" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={
                  exportLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#0F2942] border-t-transparent" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )
                }
                onClick={handleExportClick}
                disabled={exportLoading}
                className={`flex-shrink-0 ${selectedRows.size > 0 ? 'bg-blue-50 border-blue-200 text-[#0F2942]' : ''}`}
                aria-label={
                  selectedRows.size > 0
                    ? `Export ${selectedRows.size} selected employees`
                    : 'Export employees'
                }
              >
                {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleEmployeesSelectedFormatExport}
              />
            </div>
          </PermissionGate>
          {selectedRows.size > 0 && (
            <div className="flex items-center space-x-2">
              <PermissionGate permission="employee_management" action="update">
                <Button
                  variant="outline"
                  className="text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700"
                  icon={<UserCheck className="h-4 w-4" />}
                  onClick={() => handleBulkActionClicked('activate')}
                >
                  Activate ({selectedRows.size})
                </Button>
                <Button
                  variant="outline"
                  className="text-yellow-600 border-yellow-200 hover:bg-yellow-50 hover:text-yellow-700"
                  icon={<UserX className="h-4 w-4" />}
                  onClick={() => handleBulkActionClicked('deactivate')}
                >
                  Inactive ({selectedRows.size})
                </Button>
              </PermissionGate>
              <PermissionGate permission="employee_management" action="delete">
                <Button
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  icon={<Trash2 className="h-4 w-4" />}
                  onClick={() => handleBulkActionClicked('delete')}
                >
                  Delete ({selectedRows.size})
                </Button>
              </PermissionGate>
            </div>
          )}
          <PermissionGate permission="employee_management" action="create">
            <Button
              variant="outline"
              icon={
                syncingUsers ? (
                  <RefreshCw className="h-4 w-4 animate-spin text-[#0F2942]" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )
              }
              onClick={handleSyncUsers}
              disabled={syncingUsers}
              className="flex-shrink-0"
              title="Sync new users from MSSQL database"
            >
              {syncingUsers ? 'Syncing Users...' : 'Sync Users'}
            </Button>
            <button
              onClick={handleAddEmployee}
              className="flex items-center px-3 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors text-sm ml-2"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Employee
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4 w-full">
          {/* Search */}
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by Employee ID , Name or Email ID"
              value={searchTerm}
              onChange={handleSearch}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              onKeyDown={(e) => {
                // Store cursor position on any key press
                if (searchInputRef.current) {
                  cursorPositionRef.current = searchInputRef.current.selectionStart;
                }

                // Allow alphanumeric, spaces, and email characters (@, ., -, _, +)
                // Also allow navigation and control keys
                const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
                const isAllowedKey = allowedKeys.includes(e.key);
                const isAllowedChar = /^[a-zA-Z0-9\s@._+-]$/.test(e.key);

                if (!isAllowedKey && !isAllowedChar && !e.ctrlKey && !e.metaKey) {
                  e.preventDefault();
                }
              }}
              onKeyUp={(e) => {
                // Update cursor position after key is released
                if (searchInputRef.current) {
                  cursorPositionRef.current = searchInputRef.current.selectionStart;
                }
              }}
              maxLength={100}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
              autoComplete="off"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  updateEmpFilter({ search: '' });
                  if (searchInputRef.current) {
                    searchInputRef.current.focus();
                  }
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Filters */}
          {/* Department Filter */}
          <div className="flex-shrink-0 relative" ref={deptDropdownRef}>
            <button
              onClick={() => setIsDeptDropdownOpen(!isDeptDropdownOpen)}
              className="flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand min-w-[180px] max-w-[250px] bg-white text-sm"
            >
              <span className="truncate mr-2">
                {filters.department || "All Departments"}
              </span>
              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isDeptDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isDeptDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 w-full min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                <div className="py-1">
                  {filterOptions.departments.map((dept) => (
                    <button
                      key={dept}
                      onClick={() => {
                        handleFilterChange("department", dept === "All Departments" ? "" : dept);
                        setIsDeptDropdownOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${(dept === "All Departments" && !filters.department) || (filters.department === dept)
                        ? "bg-green-50 text-agrosaf-green font-medium"
                        : "text-gray-700"
                        }`}
                    >
                      {dept}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Location Filter */}
          {/* Location Filter removed */}

          {/* Status Filter */}
          <div className="flex-shrink-0">
            <select
              value={filters.team}
              onChange={(e) => handleFilterChange("team", e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green min-w-[150px]"
            >
              {filterOptions.teams.map((team) => (
                <option key={team} value={team === "All Teams" ? "" : team}>
                  {team}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-shrink-0">
            <select
              value={
                filterOptions.statuses.find(s => s.toLowerCase() === (filters.status || '').toLowerCase()) ||
                (filters.status === "All Status" ? "" : filters.status) || ""
              }
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand min-w-[150px]"
            >
              {filterOptions.statuses.map((status) => (
                <option key={status} value={status === "All Status" ? "" : status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range Filter */}
          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              inputClassName="input-field text-sm w-36 cursor-pointer outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
              onChange={(range) => {
                setDateRange(range);
              }}
            />
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <div className="relative flex-shrink-0">
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={handleClearFilters}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Employee Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-transparent" style={{ backgroundColor: 'rgba(59, 130, 246, 0.14)' }}>
              <tr>
                <th className="px-3 py-3 text-left">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green cursor-pointer"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort("employeeId")}
                >
                  <div className="flex items-center space-x-1">
                    <span>ID</span>
                    {sortField === "employeeId" ? (
                      sortDirection === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort("name")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Name</span>
                    {sortField === "name" ? (
                      sortDirection === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort("department")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Dept</span>
                    {sortField === "department" ? (
                      sortDirection === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort("role")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Role</span>
                    {sortField === "role" ? (
                      sortDirection === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                {/* Location Header removed */}
                <th
                  className="px-3 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort("doj")}
                >
                  <div className="flex items-center space-x-1">
                    <span>DOJ</span>
                    {sortField === "doj" ? (
                      sortDirection === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort("status")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    {sortField === "status" ? (
                      sortDirection === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider w-32">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                /* Skeleton Loading Rows */
                [...Array(itemsPerPage)].map((_, index) => (
                  <tr key={`skeleton-${index}`} className="animate-pulse">
                    <td className="px-3 py-4"><div className="h-4 w-4 bg-gray-200 rounded"></div></td>
                    <td className="px-3 py-4"><div className="h-4 w-16 bg-gray-200 rounded"></div></td>
                    <td className="px-3 py-4">
                      <div className="flex items-center">
                        <div className="h-8 w-8 bg-gray-200 rounded-full mr-3"></div>
                        <div className="space-y-2">
                          <div className="h-4 w-32 bg-gray-200 rounded"></div>
                          <div className="h-3 w-24 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4"><div className="h-4 w-24 bg-gray-200 rounded"></div></td>
                    <td className="px-3 py-4"><div className="h-4 w-24 bg-gray-200 rounded"></div></td>
                    <td className="px-3 py-4"><div className="h-4 w-20 bg-gray-200 rounded"></div></td>
                    <td className="px-3 py-4"><div className="h-6 w-16 bg-gray-200 rounded-full"></div></td>
                    <td className="px-3 py-4"><div className="h-8 w-24 bg-gray-200 rounded"></div></td>
                  </tr>
                ))
              ) : currentEmployees.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center">
                    <div className="text-gray-500">
                      <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <h3 className="text-lg font-medium mb-2">
                        No employees found
                      </h3>
                      <p className="text-sm">
                        {searchTerm || Object.values(filters).some((f) => f) || dateRange.startDate || dateRange.endDate
                          ? "Try adjusting your search or filters"
                          : "Get started by adding your first employee"}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                currentEmployees.map((employee) => (
                  <tr key={employee.id} className="hover:bg-gray-50">
                    <td className="px-3 py-3 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green cursor-pointer"
                        checked={selectedRows.has(employee.id)}
                        onChange={() => handleSelectRow(employee.id)}
                      />
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 truncate" title={employee.employeeId}>
                        {employee.employeeId}
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap w-80">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-6 w-6 relative">
                          <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center absolute top-0 left-0">
                            <User className="h-3 w-3 text-gray-500" />
                          </div>
                          {employee.avatar && (
                            <img
                              className="h-6 w-6 rounded-full object-cover relative z-10"
                              src={employee.avatar}
                              alt={employee.name}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                        </div>
                        <div className="ml-2 min-w-0 flex-1 max-w-[18rem]">
                          <div className="text-sm font-medium text-gray-900 truncate" title={employee.name}>
                            {employee.name}
                          </div>
                          <div className="text-xs text-gray-500 truncate" title={employee.email}>
                            {employee.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900 truncate max-w-[120px]" title={employee.department}>
                        {employee.department}
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900 truncate" title={employee.role}>
                        {employee.role}
                      </div>
                    </td>
                    {/* Location Cell removed */}
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center">
                        <Calendar className="h-3 w-3 mr-1 text-gray-400 flex-shrink-0" />
                        <span
                          className="truncate"
                          title={employee.doj ? new Date(employee.doj).toLocaleDateString() : ""}
                        >
                          {employee.doj
                            ? formatDate(employee.doj, { includeTime: false })
                            : ""}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${getStatusColor(
                          employee.status
                        )}`}
                      >
                        {getDisplayStatus(employee.status)}
                      </span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-1">
                        <PermissionGate permission="employee_management" action="read">
                          <button
                            onClick={() =>
                              handleViewEmployee(employee.employeeId)
                            }
                            className="text-agrosaf-green hover:text-agrosaf-darkGreen transition-colors p-1"
                            title="View"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        {getDisplayStatus(employee.status) !== 'Exited' && (
                          <>
                            <PermissionGate permission="employee_management" action="update">
                              <button
                                onClick={() =>
                                  handleEditEmployee(employee.employeeId)
                                }
                                className="text-[#0F2942] hover:text-blue-800 transition-colors p-1"
                                title="Edit"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                            <PermissionGate permission="employee_management" action="update">
                              <button
                                onClick={() =>
                                  handleManageDocuments(employee.employeeId)
                                }
                                className={`transition-colors p-1 relative ${employee.hasDocuments
                                  ? "text-indigo-600 hover:text-indigo-800"
                                  : "text-red-500 hover:text-red-700"
                                  }`}
                                title={employee.hasDocuments ? "Manage Documents" : "No documents submitted"}
                              >
                                <div className="relative">
                                  <FileText className="h-4 w-4" />
                                  {!employee.hasDocuments && (
                                    <AlertCircle className="h-2 w-2 text-red-600 absolute -top-1 -right-1 fill-white" />
                                  )}
                                </div>
                              </button>
                            </PermissionGate>
                            <PermissionGate permission="employee_management" action={employee.status === 'Active' ? 'delete' : 'update'}>
                              <button
                                onClick={(e) =>
                                  handleDeactivateEmployee(employee.employeeId, employee.status, e)
                                }
                                disabled={
                                  deactivatingEmployee === employee.employeeId
                                }
                                className={`transition-colors p-1 ${deactivatingEmployee === employee.employeeId
                                  ? "text-gray-400 cursor-not-allowed"
                                  : employee.status === 'Active'
                                    ? "text-red-600 hover:text-red-800"
                                    : "text-green-600 hover:text-green-800"
                                  }`}
                                title={
                                  deactivatingEmployee === employee.employeeId
                                    ? employee.status === 'Active' ? "Deactivating..." : "Activating..."
                                    : employee.status === 'Active' ? "Deactivate" : "Activate"
                                }
                              >
                                {deactivatingEmployee === employee.employeeId ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                                ) : employee.status === 'Active' ? (
                                  <UserX className="h-4 w-4" />
                                ) : (
                                  <UserCheck className="h-4 w-4" />
                                )}
                              </button>
                            </PermissionGate>
                            <PermissionGate permission="employee_management" action="delete">
                              <button
                                onClick={(e) => handleDeleteEmployee(employee.employeeId, e)}
                                className="text-red-500 hover:text-red-700 transition-colors p-1"
                                title="Delete Permanently"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalEmployees}
            limit={itemsPerPage}
            hasNextPage={currentPage < totalPages && !loading && !isMutating}
            hasPrevPage={currentPage > 1 && !loading && !isMutating}
            onPageChange={handlePageChange}
            onLimitChange={handleItemsPerPageChange}
            limitOptions={[10, 20, 50, 100]}
            border={false}
          />
        </div>


        {showConfirmDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <div className={`flex-shrink-0 w-10 h-10 mx-auto rounded-full flex items-center justify-center ${actionType === 'activate' ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                  {actionType === 'activate' ? (
                    <UserCheck className="h-6 w-6 text-green-600" />
                  ) : actionType === 'delete' ? (
                    <Trash2 className="h-6 w-6 text-red-600" />
                  ) : (
                    <UserX className="h-6 w-6 text-red-600" />
                  )}
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Confirm {actionType === 'activate' ? 'Activation' : actionType === 'delete' ? 'Permanent Deletion' : 'Deactivation'}
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  {actionType === 'activate'
                    ? isBulkDelete
                      ? `Are you sure you want to activate ${selectedRows.size} selected employees? This will restore their system access.`
                      : 'Are you sure you want to activate this employee? This will restore their system access and make them active again.'
                    : actionType === 'delete'
                      ? isBulkDelete
                        ? `Are you sure you want to PERMANENTLY delete ${selectedRows.size} selected employees? This action cannot be undone.`
                        : 'Are you sure you want to PERMANENTLY delete this employee? This action cannot be undone.'
                      : isBulkDelete
                        ? `Are you sure you want to deactivate ${selectedRows.size} selected employees? They will not be able to access the system.`
                        : 'Are you sure you want to deactivate this employee? This action will make the employee inactive and they will not be able to access the system.'}
                </p>
                <div className="flex space-x-3 justify-end">
                  <button
                    onClick={cancelDeactivate}
                    disabled={isMutating}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    No
                  </button>
                  <PermissionGate permission="employee_management" action={actionType === 'activate' ? 'update' : 'delete'}>
                    <button
                      onClick={confirmDeactivate}
                      disabled={isMutating}
                      className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${actionType === 'activate'
                        ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                        : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                        } disabled:opacity-50`}
                    >
                      {isMutating
                        ? (actionType === 'activate' ? 'Activating...' : actionType === 'delete' ? 'Deleting...' : 'Deactivating...')
                        : `Yes, ${actionType === 'activate' ? 'Activate' : actionType === 'delete' ? 'Delete Permanently' : 'Deactivate'}`
                      }
                    </button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Export Modal */}
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          loading={exportLoading}
          title="Export Employees"
          showDateRange
          scopedFormatExport={{
            onExport: handleEmployeesScopedExport,
            formats: ['csv', 'xlsx', 'pdf'],
            showCurrentPage: true,
            showFiltered: true,
            showDateRange: true,
            showAll: true,
          }}
        />
      </div>
    </div>
  );
};

export default EmployeeList;
